const ISO_MONTH_ONLY_PATTERN = /^(\d{4})-(\d{2})$/;
const ISO_DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T/;

export const MIN_PIXELS_PER_TICK = 84;
export const MIN_VISIBLE_TICK_COUNT = 2;
export const MAX_VISIBLE_TICK_COUNT = 10;
export const SIMPLE_POINT_THRESHOLD = 8;
export const SIMPLE_SERIES_THRESHOLD = 2;
export const DENSE_POINT_THRESHOLD = 16;
export const DENSE_SERIES_THRESHOLD = 4;

const COMPACT_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

const COMPACT_MONTH_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  timeZone: 'UTC',
});

const COMPACT_DATE_WITH_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

const COMPACT_MONTH_WITH_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const FULL_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

const FULL_MONTH_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

type AxisValue = string | number;
export type ChartDensityMode = 'simple' | 'standard' | 'dense';
type DateLabelPrecision = 'month' | 'day';
type ParsedDateLabel = {
  date: Date;
  precision: DateLabelPrecision;
};

const parseDateLikeLabel = (label: string): ParsedDateLabel | null => {
  const monthOnlyMatch = label.match(ISO_MONTH_ONLY_PATTERN);
  if (monthOnlyMatch) {
    const [, year, month] = monthOnlyMatch;
    return {
      date: new Date(Date.UTC(Number(year), Number(month) - 1, 1)),
      precision: 'month',
    };
  }

  const dateOnlyMatch = label.match(ISO_DATE_ONLY_PATTERN);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return {
      date: new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))),
      precision: 'day',
    };
  }

  if (!ISO_DATE_TIME_PATTERN.test(label)) {
    return null;
  }

  const timestamp = Date.parse(label);
  if (Number.isNaN(timestamp)) {
    return null;
  }

  return {
    date: new Date(timestamp),
    precision: 'day',
  };
};

export const buildVisibleTickValues = <T extends AxisValue>(
  values: T[],
  chartWidth: number,
): T[] => {
  if (values.length <= MIN_VISIBLE_TICK_COUNT) {
    return values;
  }

  const safeWidth =
    Number.isFinite(chartWidth) && chartWidth > 0
      ? chartWidth
      : MIN_PIXELS_PER_TICK * MAX_VISIBLE_TICK_COUNT;

  const maxVisibleTicks = Math.min(
    MAX_VISIBLE_TICK_COUNT,
    Math.max(MIN_VISIBLE_TICK_COUNT, Math.floor(safeWidth / MIN_PIXELS_PER_TICK)),
  );

  if (values.length <= maxVisibleTicks) {
    return values;
  }

  const lastIndex = values.length - 1;
  const indexes = new Set<number>();

  for (let index = 0; index < maxVisibleTicks; index += 1) {
    indexes.add(Math.round((lastIndex * index) / (maxVisibleTicks - 1)));
  }

  return Array.from(indexes)
    .sort((left, right) => left - right)
    .map((index) => values[index]);
};

export const buildCompactDateLabelFormatter = (labels: string[]) => {
  if (!labels.length) {
    return null;
  }

  const parsedDates = labels.map(parseDateLikeLabel);
  if (parsedDates.some((parsedDate) => !parsedDate)) {
    return null;
  }

  const years = new Set(parsedDates.map((parsedDate) => parsedDate!.date.getUTCFullYear()));
  const includesMultipleYears = years.size > 1;

  return (value: unknown) => {
    const parsedDate = parseDateLikeLabel(String(value ?? ''));
    if (!parsedDate) {
      return String(value ?? '');
    }

    if (parsedDate.precision === 'month') {
      const formatter = includesMultipleYears
        ? COMPACT_MONTH_WITH_YEAR_FORMATTER
        : COMPACT_MONTH_FORMATTER;
      return formatter.format(parsedDate.date);
    }

    const formatter = includesMultipleYears
      ? COMPACT_DATE_WITH_YEAR_FORMATTER
      : COMPACT_DATE_FORMATTER;
    return formatter.format(parsedDate.date);
  };
};

export const buildTooltipDateLabelFormatter = (labels: string[]) => {
  const compactFormatter = buildCompactDateLabelFormatter(labels);
  if (!compactFormatter) {
    return null;
  }

  return (value: unknown) => {
    const parsedDate = parseDateLikeLabel(String(value ?? ''));
    if (!parsedDate) {
      return String(value ?? '');
    }

    return parsedDate.precision === 'month'
      ? FULL_MONTH_FORMATTER.format(parsedDate.date)
      : FULL_DATE_FORMATTER.format(parsedDate.date);
  };
};

export const getChartDensityMode = (pointCount: number, seriesCount: number): ChartDensityMode => {
  if (pointCount >= DENSE_POINT_THRESHOLD || seriesCount >= DENSE_SERIES_THRESHOLD) {
    return 'dense';
  }

  if (pointCount <= SIMPLE_POINT_THRESHOLD && seriesCount <= SIMPLE_SERIES_THRESHOLD) {
    return 'simple';
  }

  return 'standard';
};

export const shouldHideLineDots = (pointCount: number, seriesCount: number): boolean =>
  getChartDensityMode(pointCount, seriesCount) === 'dense';

export const shouldShowDensityHint = (params: {
  labelCount: number;
  visibleTickCount: number;
  areDotsHidden: boolean;
  areLabelsTruncated: boolean;
}): boolean => {
  const { labelCount, visibleTickCount, areDotsHidden, areLabelsTruncated } = params;

  return visibleTickCount < labelCount || areDotsHidden || areLabelsTruncated;
};
